import useRepo from '@/nebula/repo';
import { RESULT, UID } from 'shared/api/query/constants';
import { buildCustomFuncProgressCoursePred } from '@/api/query/custom-func-helper';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { ref, watch } from 'vue';
import { reverseEdge } from 'shared/api/query/edges';

// Normalize ISO date to match Go's time.RFC3339 format:
// - strip fractional seconds (.000)
// - normalize +00:00 to Z (Go uses Z for UTC, JS uses +00:00)
const toRFC3339 = (isoDate) => isoDate.replace(/\.\d+/, '').replace(/\+00:00$/, 'Z');

export default function useCompareToSnapshots(goalUids, compareToDate) {
  const repo = useRepo('goalProgressSeries');
  const loading = ref(false);
  const pending = new Set();

  const snapshotKey = (goalUid, dateStr) => `${goalUid}:${toRFC3339(dateStr)}`;

  const getSnapshot = (goalUid) => {
    if (!compareToDate.value) {
      return undefined;
    }
    return repo.selectSingle(snapshotKey(goalUid, compareToDate.value));
  };

  watch([goalUids, compareToDate], ([uids, date]) => {
    if (!date || !uids || uids.length === 0) {
      return;
    }

    const missing = uids.filter((uid) => {
      const key = snapshotKey(uid, date);
      return !repo.selectSingle(key) && !pending.has(key);
    });
    if (missing.length === 0) {
      return;
    }

    const missingKeys = missing.map((uid) => snapshotKey(uid, date));
    missingKeys.forEach((key) => pending.add(key));

    loading.value = true;
    repo.query([
      {
        uid: missing,
        alias: RESULT,
        func: { name: UID },
        model: goalConfig.model,
        default: [],
        children: [
          { attr: UID },
          buildCustomFuncProgressCoursePred({ alias: 'compareTo', attr: reverseEdge(goalConfig.edges.paysOnto), timeSeries: [date] }),
        ],
      },
    ]).finally(() => {
      missingKeys.forEach((key) => pending.delete(key));
      loading.value = false;
    });
  }, { immediate: true });

  return { getSnapshot, loading };
}
